import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Card, CardBody, Button, Progress, UncontrolledTooltip, Alert } from 'reactstrap';
import WavePlayer from '../../WavePlayer';
import { getDownloadURL } from 'firebase/storage';
import { withFirebase } from '../../Firebase';
import FormControl from '@material-ui/core/FormControl';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';
import CheckIcon from '@material-ui/icons/Check';
import ModalConsumer from '../../utility/Modal/ModalConsumer';
import GenericConfirmModal from '../../GenericModal/ConfirmModal';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { FILE_CONFIG, ERROR_CONFIG } from '../../../config';
import { useOrganizationStore } from '../../../store/organization.store';

// CSS for ticker effect
const tickerStyle = {
  width: '200px',
  minWidth: '200px',
  maxWidth: '200px',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  position: 'relative',
  display: 'inline-block',
  verticalAlign: 'middle',
  cursor: 'pointer',
};
const tickerInnerStyle = {
  display: 'inline-block',
  minWidth: '200px',
  willChange: 'transform',
  pointerEvents: 'none',
};
const tickerCSS = `
@keyframes ticker-scroll {
  0% { transform: translateX(0); }
  100% { transform: translateX(calc(-1 * (100% - 200px))); }
}
.ticker-hover:hover .ticker-inner {
  animation: ticker-scroll 8s linear infinite;
}
`;

// Helper style for remove button
const removeButtonStyle = {
  position: 'absolute',
  top: '8px',
  right: 4,
  color: '#fff',
  background: 'rgba(30,30,30,0.85)',
  zIndex: 2,
  pointerEvents: 'auto',
};
const removeButtonStyleVertical = {
  position: 'absolute',
  top: '50%',
  right: 4,
  transform: 'translateY(-50%)',
  color: '#fff',
  background: 'rgba(30,30,30,0.85)',
  zIndex: 2,
  pointerEvents: 'auto',
};

// Helper to group files by base name (for single mode too)
function groupFilesByBaseNameAll(files) {
  const groups = {};
  files.forEach(file => {
    const base = file.name.replace(FILE_CONFIG.AUDIO.EXTENSION_PATTERN, '');
    if (!groups[base]) groups[base] = {};
    if (file.name.toLowerCase().endsWith('.wav')) groups[base].wav = file;
    if (file.name.toLowerCase().endsWith('.mp3')) groups[base].mp3 = file;
  });
  return groups;
}

// Track type options from configuration
const TRACK_TYPE_OPTIONS = FILE_CONFIG.TRACK_TYPES;

const MultiFileUploader = withFirebase(({ onUploadSuccess, toggleFileSelected, documentRef, id, firebase, onStatusChange, onModeChange, batchTracks, onBatchFilesSelected, singleTrackId, onSingleFilesSelected, isEditMode, onSingleTrackNameEdit, onApplyTrackNameToFirstPair, onApplyTrackNameToBatchPair, canonicalTrackName, autoAnalyzeAudio, onAudioAnalysis, fileVersion, qcVersion, uploadDisabled, uploadDisabledMessage }) => {
  const fileInputRef = useRef();
  const [queuedFileGroups, setQueuedFileGroups] = useState({}); // files/pairs selected but not yet uploading

  // Get current organization for multi-tenant storage
  const currentOrgId = useOrganizationStore(state => state.currentOrgId);
  const [currentUploadFileGroups, setCurrentUploadFileGroups] = useState({}); // files/pairs currently uploading
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [uploadedTracks, setUploadedTracks] = useState([]); // persistent list of uploaded tracks
  const [uploadMode, setUploadMode] = useState('single'); // 'single' is now the default
  const [pendingMode, setPendingMode] = useState(null); // for mode change confirmation
  const [showModeConfirm, setShowModeConfirm] = useState(false);
  const [showBatchToast, setShowBatchToast] = useState(false);
  const [batchToastCount, setBatchToastCount] = useState(0);
  const [localBatchTracks, setLocalBatchTracks] = useState([]); // robust local batch queue
  // Inline edit state for batch track names
  const [editingName, setEditingName] = useState(null); // base currently being edited
  const [editedName, setEditedName] = useState('');
  // Add these hooks here:
  const [trackTypes, setTrackTypes] = useState({}); // { base: type }
  const [menuAnchorEls, setMenuAnchorEls] = useState({}); // { base: anchorEl }
  // Add state for batch checkmark
  const [batchCheckedIndex, setBatchCheckedIndex] = useState(0); // index of checked pair in batch mode
  // Add state for Full Track toast
  const [showFullTrackToast, setShowFullTrackToast] = useState(false);
  
  // Audio analysis state
  const [analyzedFiles, setAnalyzedFiles] = useState(new Set()); // Track which files have been analyzed

  // Add a ref to track the next order index (must be inside component)
  const nextOrderRef = useRef(0);

  // Add a ref to prevent infinite loop when syncing from parent
  const syncingTrackNameRef = useRef(false);

  // Add a ref to expose the batch name setter to parent/chatbot
  const batchNameSetterRef = useRef();

  // Helper to group files by base name and assign order (for batch mode)
  function groupFilesByBaseNameWithOrder(files, prevGroups = {}) {
    const groups = { ...prevGroups };
    files.forEach(file => {
      const base = file.name.replace(FILE_CONFIG.AUDIO.EXTENSION_PATTERN, '');
      if (!groups[base]) {
        groups[base] = { order: nextOrderRef.current++ };
      }
      if (file.name.toLowerCase().endsWith('.wav')) groups[base].wav = file;
      if (file.name.toLowerCase().endsWith('.mp3')) groups[base].mp3 = file;
    });
    return groups;
  }
  
  // Audio analysis function - extract first track file for parent to analyze
  const getFirstTrackForAnalysis = useCallback((fileGroups) => {

    if (!autoAnalyzeAudio) {
      return null;
    }

    let audioFile = null;
    let trackName = '';
    let groupId = '';

    if (uploadMode === 'single') {
      // Handle single mode: files are in {single: {files: [...]}}
      const singleGroup = fileGroups.single;
      if (!singleGroup?.files || singleGroup.files.length === 0) {
        return null;
      }


      // Find MP3 or WAV file (prefer MP3)
      const mp3File = singleGroup.files.find(f => f.name.toLowerCase().endsWith('.mp3'));
      const wavFile = singleGroup.files.find(f => f.name.toLowerCase().endsWith('.wav'));
      
      audioFile = mp3File || wavFile;
      if (!audioFile) {
        return null;
      }

      // Extract track name from file name
      trackName = audioFile.name.replace(FILE_CONFIG.AUDIO.EXTENSION_PATTERN, '');
      groupId = `single-${singleGroup.files.map(f => f.name).join('-')}-${singleGroup.files.length}`;

    } else {
      // Handle batch mode: files are grouped by base name
      const firstGroupKey = Object.keys(fileGroups)[0];
      if (!firstGroupKey) {
        return null;
      }
      
      const firstGroup = fileGroups[firstGroupKey];
      
      // Prefer MP3 for analysis (smaller file size), fallback to WAV
      audioFile = firstGroup.mp3 || firstGroup.wav;
      if (!audioFile) {
        return null;
      }

      trackName = firstGroupKey;
      groupId = `${firstGroupKey}-${firstGroup.wav?.size || 0}-${firstGroup.mp3?.size || 0}`;
    }

    // Check if we've already analyzed this file group
    if (analyzedFiles.has(groupId)) {
      return null;
    }


    // Mark as analyzed
    setAnalyzedFiles(prev => new Set([...prev, groupId]));
    
    return {
      file: audioFile,
      trackName: trackName,
      groupId
    };
  }, [autoAnalyzeAudio, analyzedFiles, uploadMode]);

  // Force single mode when in edit mode
  useEffect(() => {
    if (isEditMode && uploadMode !== 'single') {
      setUploadMode('single');
      if (onModeChange) onModeChange('single');
    }
  }, [isEditMode, uploadMode, onModeChange]);

  // Sync uploadMode with parent's mode
  useEffect(() => {
    if (onModeChange) {
      onModeChange(uploadMode);
    }
  }, [uploadMode, onModeChange]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (uploadMode !== 'batch') return;
    if (Array.isArray(batchTracks) && batchTracks.length > 0) {
      setLocalBatchTracks(batchTracks);
      // Also update queuedFileGroups to match new batchTracks
      const newGroups = {};
      batchTracks.forEach(pair => {
        if (pair.base && pair.wav) {
          newGroups[pair.base] = { wav: pair.wav, ...(pair.mp3 ? { mp3: pair.mp3 } : {}), order: pair.order };
        }
      });
      setQueuedFileGroups(newGroups);
    } else {
      setLocalBatchTracks([]);
      setQueuedFileGroups({});
    }
  }, [batchTracks, uploadMode, queuedFileGroups.single?.files, trackTypes]);

  // Notify parent of upload/queue status
  const notifyStatus = (uploadingOverride) => {
    if (typeof onStatusChange === 'function') {
      const hasQueuedFiles = (uploadMode === 'batch')
        ? Object.keys(queuedFileGroups).length > 0
        : (queuedFileGroups.single?.files?.length > 0);
      onStatusChange({
        uploading: uploadingOverride !== undefined ? uploadingOverride : uploading,
        hasQueuedFiles,
      });
    }
  };

  // Call notifyStatus whenever uploading or queuedFileGroups changes
  useEffect(() => {
    notifyStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploading, queuedFileGroups, uploadMode]);

  // Handler to open dropdown
  const handleMenuOpen = (base, event) => {
    setMenuAnchorEls(prev => ({ ...prev, [base]: event.currentTarget }));
  };
  // Handler to close dropdown
  const handleMenuClose = (base) => {
    setMenuAnchorEls(prev => ({ ...prev, [base]: null }));
  };
  // Handler to select type
  const handleTypeSelect = (base, type) => {
    setTrackTypes(prev => {
      let newTypes = { ...prev };
      // If setting to "Full Track", remove it from all others
      if (type === "Full Track") {
        Object.keys(newTypes).forEach(b => {
          if (b !== base && newTypes[b] === "Full Track") {
            newTypes[b] = "";
          }
        });
      }
      newTypes[base] = type;
      // After updating type, update all pair names to match new types
      setQueuedFileGroups(prevQFG => {
        const files = prevQFG.single?.files || [];
        const groups = groupFilesByBaseNameAll(files);
        // Find the current full track base
        let fullTrackBase = Object.entries(newTypes).find(([b, t]) => t === 'Full Track' && groups[b])?.[0] || Object.keys(groups)[0];
        // --- Use canonicalTrackName as the new base if setting to Full Track ---
        if (type === 'Full Track' && canonicalTrackName) {
          fullTrackBase = canonicalTrackName;
        }
        if (!fullTrackBase) return prevQFG;
        const { single, newTrackTypes } = updateAllPairNamesOnFullTrackRename(fullTrackBase, newTypes, prevQFG, canonicalTrackName, base, type);
        // --- Begin fix: update trackTypes to use new base keys ---
        setTrackTypes(currentTypes => {
          // Remove old keys that no longer exist in newTrackTypes
          const updated = { ...currentTypes };
          Object.keys(updated).forEach(k => {
            if (!(k in newTrackTypes)) {
              delete updated[k];
            }
          });
          // Add/overwrite with newTrackTypes
          Object.entries(newTrackTypes).forEach(([k, v]) => {
            updated[k] = v;
          });
          return updated;
        });
        // --- End fix ---
        return { single };
      });
      return newTypes;
    });
    handleMenuClose(base);
  };

  // Handle file selection
  const handleFiles = (fileList) => {
    // Add originalName property to each file if not already present
    const files = Array.from(fileList).map(f => {
      if (!f.originalName) {
        try {
          Object.defineProperty(f, 'originalName', { value: f.name, writable: false, configurable: true });
        } catch (e) {
          // fallback for browsers that don't allow defineProperty on File
          f.originalName = f.name;
        }
      }
      return f;
    });
    // Auto-detect mode: if uploader is empty and multiple WAVs are added, switch to batch
    if (!isEditMode && uploadMode === 'single' && !hasQueuedOrUploaded()) {
      const wavCount = files.filter(f => f.name.toLowerCase().endsWith('.wav')).length;
      if (wavCount > 1) {
        setUploadMode('batch');
        setError('');
        if (onModeChange) onModeChange('batch');
        // Re-run handleFiles in batch mode by continuing below with updated mode
        // Since setState is async, we process directly as batch here
        const groups = groupFilesByBaseNameWithOrder(files, {});
        const noWavFiles = Object.values(groups).every(g => !g.wav);
        if (noWavFiles) {
          setError('Please upload at least one WAV file.');
          return;
        }
        setQueuedFileGroups(groups);
        const pairCount = Object.keys(groups).length;
        if (pairCount > 1) {
          setBatchToastCount(pairCount);
          setShowBatchToast(true);
          setTimeout(() => setShowBatchToast(false), ERROR_CONFIG.TOAST.DURATION);
        }
        const filePairs = Object.entries(groups).map(([base, pair]) => ({ base, wav: pair.wav, mp3: pair.mp3 }));
        if (onBatchFilesSelected) onBatchFilesSelected(filePairs);
        return;
      }
    }

    let newFileGroups;
    if (uploadMode === 'batch') {
      const groups = groupFilesByBaseNameWithOrder(files, queuedFileGroups);
      // Check that at least WAV files are provided (MP3 will be auto-generated if missing)
      const noWavFiles = Object.values(groups).every(g => !g.wav);
      if (noWavFiles) {
        setError('Please upload at least one WAV file.');
        setQueuedFileGroups({});
        setLocalBatchTracks([]);
        if (onBatchFilesSelected) onBatchFilesSelected([]);
        return;
      }
      setError('');
      newFileGroups = groups;
      const pairCount = Object.keys(groups).length;
      if (pairCount > 1) {
        setBatchToastCount(pairCount);
        setShowBatchToast(true);
        setTimeout(() => setShowBatchToast(false), ERROR_CONFIG.TOAST.DURATION);
      }
      // Prepare array of { base, wav, mp3 } for parent
      const filePairs = Object.entries(groups).map(([base, pair]) => ({ base, wav: pair.wav, mp3: pair.mp3 }));
      if (onBatchFilesSelected) onBatchFilesSelected(filePairs);
    } else {
      // Single mode: limit to one WAV (and optionally one MP3) per spot
      // If only WAV is provided, MP3 will be auto-generated during upload
      setError('');
      const allFiles = [...files];
      const mp3s = allFiles.filter(f => f.name.toLowerCase().endsWith('.mp3'));
      const wavs = allFiles.filter(f => f.name.toLowerCase().endsWith('.wav'));
      if (mp3s.length > 1 || wavs.length > 1) {
        setError('Single mode allows one WAV and one MP3 per spot. Use Batch mode for multiple files.');
        return;
      }
      if (wavs.length === 0) {
        setError('Please upload a WAV file. An MP3 preview will be generated automatically.');
        return;
      }
      // Replace existing files of the same type (not stack)
      const existing = queuedFileGroups.single?.files || [];
      const existingMp3s = existing.filter(f => f.name.toLowerCase().endsWith('.mp3'));
      const existingWavs = existing.filter(f => f.name.toLowerCase().endsWith('.wav'));
      if ((mp3s.length > 0 && existingMp3s.length > 0) || (wavs.length > 0 && existingWavs.length > 0)) {
        // Replace: keep only the new file for that type
        const merged = [
          ...(mp3s.length > 0 ? mp3s : existingMp3s),
          ...(wavs.length > 0 ? wavs : existingWavs),
        ];
        newFileGroups = { single: { files: merged } };
      } else {
        newFileGroups = { single: { files: [...existing, ...allFiles] } };
      }
      setLocalBatchTracks([]); // clear batch queue in single mode
      if (onBatchFilesSelected) onBatchFilesSelected([]);
      if (onSingleFilesSelected) onSingleFilesSelected(newFileGroups.single.files);
      // Force all pairs to "Full Track" in single mode (no Variation labels)
      const singleGroups = groupFilesByBaseNameAll(newFileGroups.single.files);
      const forcedTypes = {};
      Object.keys(singleGroups).forEach(base => { forcedTypes[base] = 'Full Track'; });
      setTrackTypes(forcedTypes);
      // Set Track Name field to first pair's base name if not already set
      if (typeof onSingleTrackNameEdit === 'function') {
        const firstBase = Object.keys(singleGroups)[0];
        if (firstBase) {
          onSingleTrackNameEdit(firstBase);
        }
      }
    }
    setQueuedFileGroups(newFileGroups);
    if (fileInputRef.current) fileInputRef.current.value = '';
    
    
    // Trigger audio analysis for the first track if auto-analysis is enabled
    if (autoAnalyzeAudio && Object.keys(newFileGroups).length > 0 && onAudioAnalysis) {
      const trackToAnalyze = getFirstTrackForAnalysis(newFileGroups);
      if (trackToAnalyze) {
        onAudioAnalysis(trackToAnalyze);
      } else {
      }
    } else {
    }
    
    notifyStatus();
  };

  // Handle drag and drop
  const handleDrop = (e) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  // Handle click to open file dialog
  const handleClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  // Handle file input change
  const handleChange = (e) => {
    handleFiles(e.target.files);
  };

  // Handle file upload
  const handleUpload = async () => {
    setUploading(true);
    notifyStatus(true);
    const progress = {};
    const uploadedData = {};
    let newTracks = [];
    setCurrentUploadFileGroups(queuedFileGroups);

    if (uploadMode === 'batch') {
      const QC_SERVICE_URL = process.env.REACT_APP_QC_SERVICE_URL || 'http://localhost:8080';
      // Use localBatchTracks as the source of truth (set from parent batchTracks)
      for (const track of localBatchTracks) {
        let { id: trackId, base, wav, mp3 } = track;
        if (!trackId || !wav) continue; // skip tracks without WAV (MP3 is optional — auto-generated)
        uploadedData[base] = {};
        const version = fileVersion || 1;

        // Auto-generate MP3 from WAV if no MP3 provided
        if (!mp3) {
          try {
            const mp3DisplayName = `${base}.mp3`;
            progress[mp3DisplayName] = 0;
            setUploadProgress({ ...progress });

            const formData = new FormData();
            formData.append('file', wav, `${base}.wav`);

            const convertResponse = await fetch(`${QC_SERVICE_URL}/convert-to-mp3`, {
              method: 'POST',
              body: formData,
            });

            if (!convertResponse.ok) {
              throw new Error(`MP3 conversion failed: ${convertResponse.status}`);
            }

            const mp3Blob = await convertResponse.blob();
            mp3 = new File([mp3Blob], mp3DisplayName, { type: 'audio/mpeg' });
            progress[mp3DisplayName] = 50;
            setUploadProgress({ ...progress });
          } catch (err) {
            setError(`MP3 conversion failed for ${base}. You can upload an MP3 manually.`);
            // Continue with WAV only
          }
        }

        // Upload WAV and MP3
        const filesToUpload = [{ ext: 'wav', file: wav }];
        if (mp3) filesToUpload.push({ ext: 'mp3', file: mp3 });

        const batchQcVer = qcVersion || 1;
        for (const { ext, file } of filesToUpload) {
          const displayName = `${base}.${ext}`;
          const versionedName = `${base}_v${version}_qc${batchQcVer}.${ext}`;
          let fileToUpload = file;
          try {
            fileToUpload = new File([file], versionedName, { type: file.type });
          } catch (e) {
            fileToUpload = file;
          }
          const fileData = {
            file: fileToUpload,
            name: currentOrgId ? versionedName : `${trackId}/${versionedName}`,
            metadata: {
              contentType: file.type,
              customMetadata: {
                ref: trackId,
                version: String(version),
              },
            },
          };
          progress[displayName] = progress[displayName] || 0;
          setUploadProgress({ ...progress });
          try {
            const snapshot = currentOrgId
              ? await firebase.doOrgUpload(currentOrgId, trackId, fileData)
              : await firebase.doUpload(fileData);
            progress[displayName] = 100;
            setUploadProgress({ ...progress });
            const downloadUrl = await getDownloadURL(snapshot.ref);
            uploadedData[base][ext] = {
              downloadUrl,
              storageRef: versionedName,
              fileName: displayName,
              version: version,
              qcVersion: batchQcVer,
              // Byte size so org storage usage can be summed on the
              // dashboard without listing Storage. New uploads only —
              // legacy entries omit it and count as 0.
              size: file.size,
            };
          } catch (err) {
            setError(`Upload failed for ${displayName}`);
            setUploading(false);
            setCurrentUploadFileGroups({});
            notifyStatus(false);
            return;
          }
        }
        onUploadSuccess && onUploadSuccess({
          id: trackId,
          base,
          wav: uploadedData[base].wav,
          mp3: uploadedData[base].mp3 || null,
        });
        newTracks.push({
          mode: 'batch',
          id: trackId,
          base,
          wav: uploadedData[base].wav,
          mp3: uploadedData[base].mp3 || null,
        });
      }
      setLocalBatchTracks([]); // clear after upload
    } else {
      // Single mode
      let files = queuedFileGroups.single?.files || [];
      let uploadedFiles = [];
      
      // Always ensure we have a valid track ID
      //console.log('[MultiFileUploader handleUpload] singleTrackId:', singleTrackId);
      if (!singleTrackId) {
        setError('No track ID set for single upload.');
        setUploading(false);
        setCurrentUploadFileGroups({});
        notifyStatus(false);
        return;
      }


      // Check if we need to auto-generate MP3
      const wavOnlyFiles = files.filter(f => f.name.toLowerCase().endsWith('.wav'));
      const manualMp3Files = files.filter(f => f.name.toLowerCase().endsWith('.mp3'));
      const needsAutoMp3 = wavOnlyFiles.length > 0 && manualMp3Files.length === 0;

      // If WAV-only, convert to MP3 from local blob BEFORE uploading (much faster than round-trip via Storage)
      if (needsAutoMp3 && wavOnlyFiles[0]) {
        try {
          const wavLocalFile = wavOnlyFiles[0];
          const mp3DisplayName = wavLocalFile.name.replace(/\.wav$/i, '.mp3');
          progress[mp3DisplayName] = 0;
          setUploadProgress({ ...progress });

          const QC_SERVICE_URL = process.env.REACT_APP_QC_SERVICE_URL || 'http://localhost:8080';
          const formData = new FormData();
          formData.append('file', wavLocalFile, wavLocalFile.name);

          const convertResponse = await fetch(`${QC_SERVICE_URL}/convert-to-mp3`, {
            method: 'POST',
            body: formData,
          });

          if (!convertResponse.ok) {
            throw new Error(`MP3 conversion failed: ${convertResponse.status}`);
          }

          const mp3Blob = await convertResponse.blob();
          progress[mp3DisplayName] = 50;
          setUploadProgress({ ...progress });

          // Create a File object for the MP3 and add it to the upload queue
          const mp3AsFile = new File([mp3Blob], mp3DisplayName, { type: 'audio/mpeg' });
          files = [...files, mp3AsFile];
        } catch (err) {
          setError('MP3 conversion failed. You can upload an MP3 manually.');
        }
      }

      for (const file of files) {
        // Build versioned filename: base_v{n}.ext or base_v{n}_qc{m}.ext
        const ext = file.name.split('.').pop().toLowerCase();
        const baseName = file.name.replace(/\.[^/.]+$/, '');
        const version = fileVersion || 1;
        const qcVer = qcVersion || 1;
        // Always include QC version: SpotName_v1_qc1.wav
        const versionedName = `${baseName}_v${version}_qc${qcVer}.${ext}`;
        const displayName = file.name; // Clean name without version suffix

        // For org-scoped uploads, doOrgUpload builds the full path: audio/{orgId}/{trackId}/
        const fileToUpload = new File([file], versionedName, { type: file.type });
        const fileData = {
          file: fileToUpload,
          name: currentOrgId ? versionedName : `${singleTrackId}/${versionedName}`,
          metadata: {
            contentType: file.type,
            customMetadata: {
              ref: singleTrackId,
              version: String(version),
              qcVersion: String(qcVer),
            },
          },
        };
        progress[displayName] = 0;
        setUploadProgress({ ...progress });
        try {
          // Use org-scoped upload if organization is selected, otherwise legacy upload
          const snapshot = currentOrgId
            ? await firebase.doOrgUpload(currentOrgId, singleTrackId, fileData)
            : await firebase.doUpload(fileData);
          progress[displayName] = 100;
          setUploadProgress({ ...progress });
          const downloadUrl = await getDownloadURL(snapshot.ref);
          uploadedFiles.push({
            downloadUrl,
            storageRef: versionedName,  // Versioned name in storage
            fileName: displayName,       // Clean name for UI display
            version: version,
            qcVersion: qcVer,
            // Byte size so org storage usage can be summed on the
            // dashboard without listing Storage. New uploads only —
            // legacy entries omit it and count as 0.
            size: file.size,
          });
        } catch (err) {
          setError(`Upload failed for ${displayName}`);
          setUploading(false);
          setCurrentUploadFileGroups({});
          notifyStatus(false);
          return;
        }
      }
      onUploadSuccess && onUploadSuccess(uploadedFiles);
      newTracks.push({
        mode: 'single',
        files: uploadedFiles,
      });
    }
    setUploadedTracks(prev => [...prev, ...newTracks]);
    setUploading(false);
    setCurrentUploadFileGroups({}); // clear uploading group after upload
    setQueuedFileGroups({}); // Now clear the queue after upload is complete
    // Don't clear analyzedFiles here - only clear when files are removed from queue
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    notifyStatus(false);
  };

  // Remove a file pair (batch) or file (single) from fileGroups
  const handleRemoveSelected = (key, ext = null) => {
    if (uploadMode === 'batch') {
      setQueuedFileGroups(prev => {
        const newGroups = { ...prev };
        delete newGroups[key];
        // If all files are removed, reset the file input and clear analyzed files
        if (Object.keys(newGroups).length === 0) {
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          // Clear the analyzed files tracking when all files are removed
          setAnalyzedFiles(new Set());
        }
        return newGroups;
      });
      // Also update localBatchTracks to remove the track with base === key
      setLocalBatchTracks(prev => prev.filter(t => t.base !== key));
    } else if (uploadMode === 'single') {
      setQueuedFileGroups(prev => {
        const files = prev.single?.files || [];
        const newFiles = files.filter((f, idx) => idx !== key);
        // If all files are removed, reset the file input and clear analyzed files
        if (newFiles.length === 0) {
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          // Clear the analyzed files tracking when all files are removed
          setAnalyzedFiles(new Set());
        }
        return { single: { files: newFiles } };
      });
    }
    // After updating queuedFileGroups, notify parent
    setTimeout(() => notifyStatus(), 0);
  };

  // Remove an uploaded track from the list
  const handleRemoveUploaded = (idx) => {
    setUploadedTracks(prev => prev.filter((_, i) => i !== idx));
  };

  // Fix 1: Remove TRACK_TYPE_OPTIONS from useCallback dependency array
  const getFirstAvailableType = useCallback((usedTypes) => {
    return TRACK_TYPE_OPTIONS.find(type => !usedTypes.includes(type)) || '';
  }, []);

  // Helper to update all pair names when Full Track name changes, using canonicalTrackName if provided
  function updateAllPairNamesOnFullTrackRename(newFullTrackBase, trackTypes, queuedFileGroups, canonicalTrackNameOverride, changingBase, changingType) {
    // Only for single mode
    const files = queuedFileGroups.single?.files || [];
    const groups = groupFilesByBaseNameAll(files);
    const newFiles = [];
    let newTrackTypes = {};
    // Use canonicalTrackNameOverride if provided and this is a Full Track change
    const baseForFullTrack = (changingType === 'Full Track' && canonicalTrackNameOverride) ? canonicalTrackNameOverride : newFullTrackBase;
    // For each group, rename as needed
    Object.entries(groups).forEach(([base, pair]) => {
      const type = trackTypes[base];
      let targetBase = base;
      if (type === 'Full Track') {
        targetBase = baseForFullTrack;
      } else if (type && type !== '') {
        targetBase = `${baseForFullTrack}_${type.replace(/\s+/g, '')}`;
      }
      // Rename both files in the pair
      ['wav', 'mp3'].forEach(ext => {
        if (pair[ext]) {
          const origName = pair[ext].originalName || pair[ext].name;
          const newFile = new File([pair[ext]], `${targetBase}.${ext}`, { type: pair[ext].type });
          try {
            Object.defineProperty(newFile, 'originalName', { value: origName, writable: false, configurable: true });
          } catch (e) {
            newFile.originalName = origName;
          }
          newFiles.push(newFile);
        }
      });
      // Persist type selection for new base
      if (type) {
        newTrackTypes[targetBase] = type;
      } else {
        // Try to infer type from the base name suffix if possible
        const suffix = targetBase.replace(baseForFullTrack, '').replace(/^_/, '');
        const matchedType = TRACK_TYPE_OPTIONS.find(opt => suffix === opt.replace(/\s+/g, ''));
        if (matchedType) {
          newTrackTypes[targetBase] = matchedType;
        }
      }
    });
    return { single: { files: newFiles }, newTrackTypes };
  }

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!canonicalTrackName) return;
    if (uploadMode !== 'single') return;
    const files = queuedFileGroups.single?.files || [];
    const groups = groupFilesByBaseNameAll(files);
    const fullTrackBase = Object.entries(trackTypes).find(([base, type]) => type === 'Full Track' && groups[base]);
    if (fullTrackBase && fullTrackBase[0] !== canonicalTrackName) {
      // Update all pair names to use the new canonical name
      setQueuedFileGroups(prevQFG => {
        const { single, newTrackTypes } = updateAllPairNamesOnFullTrackRename(canonicalTrackName, trackTypes, prevQFG, canonicalTrackName, fullTrackBase[0], 'Full Track');
        setTrackTypes(newTrackTypes);
        return { single };
      });
    }
  }, [canonicalTrackName]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Watch for changes to canonicalTrackName and update the base of the Full Track pair if needed
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!canonicalTrackName) return;
    if (uploadMode !== 'single') return;
    const files = queuedFileGroups.single?.files || [];
    const groups = groupFilesByBaseNameAll(files);
    const fullTrackBase = Object.entries(trackTypes).find(([base, type]) => type === 'Full Track' && groups[base]);
    if (fullTrackBase && fullTrackBase[0] !== canonicalTrackName) {
      // Update all pair names to use the new canonical name
      setQueuedFileGroups(prevQFG => {
        const { single, newTrackTypes } = updateAllPairNamesOnFullTrackRename(canonicalTrackName, trackTypes, prevQFG, canonicalTrackName, fullTrackBase[0], 'Full Track');
        setTrackTypes(newTrackTypes);
        return { single };
      });
    }
  }, [canonicalTrackName]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Patch updateSingleTrackName to update all pair names if full track is renamed
  const updateSingleTrackName = (oldBase, newBase, pair) => {
    setQueuedFileGroups(prev => {
      const files = prev.single?.files || [];
      const groups = groupFilesByBaseNameAll(files);
      const fullTrackBase = getFullTrackBase(trackTypes, groups);
      // If renaming the full track, update all pairs
      if (fullTrackBase && fullTrackBase === oldBase) {
        const { single, newTrackTypes } = updateAllPairNamesOnFullTrackRename(newBase, trackTypes, prev, canonicalTrackName, oldBase, 'Full Track');
        setTrackTypes(newTrackTypes); // update types to match new bases
        return { single };
      }
      // Otherwise, just rename this pair
      const newFiles = files.map(f => {
        const ext = f.name.toLowerCase().endsWith('.wav') ? 'wav' : f.name.toLowerCase().endsWith('.mp3') ? 'mp3' : '';
        if (f.name.replace(/\.(wav|mp3)$/i, '') === oldBase && ext) {
          const origName = f.originalName || f.name;
          const newFile = new File([f], `${newBase}.${ext}`, { type: f.type });
          try {
            Object.defineProperty(newFile, 'originalName', { value: origName, writable: false, configurable: true });
          } catch (e) {
            newFile.originalName = origName;
          }
          return newFile;
        }
        return f;
      });
      // Also update type mapping for renamed base
      if (trackTypes[oldBase]) {
        setTrackTypes(prevTypes => {
          const updated = { ...prevTypes };
          updated[newBase] = updated[oldBase];
          delete updated[oldBase];
          return updated;
        });
      }
      return { single: { files: newFiles } };
    });
    // Only update parent form's Track Name field if editing the 'Full Track' pair and not already syncing
    if (typeof onSingleTrackNameEdit === 'function' && !syncingTrackNameRef.current) {
      const files = queuedFileGroups.single?.files || [];
      const groups = groupFilesByBaseNameAll(files);
      const fullTrackBase = getFullTrackBase(trackTypes, groups);
      if (fullTrackBase && fullTrackBase === oldBase) {
        syncingTrackNameRef.current = true;
        onSingleTrackNameEdit(newBase);
        setTimeout(() => { syncingTrackNameRef.current = false; }, 0);
      }
    }
  };

  // Patch the exposed setter for chatbot/parent to also update all pair names
  useEffect(() => {
    if (typeof onApplyTrackNameToFirstPair === 'function') {
      onApplyTrackNameToFirstPair((name) => {
        if (!name) return;
        syncingTrackNameRef.current = true;
        setQueuedFileGroups(prev => {
          const files = prev.single?.files || [];
          if (!files.length) return prev;
          const groups = groupFilesByBaseNameAll(files);
          const fullTrackBase = getFullTrackBase(trackTypes, groups);
          if (!fullTrackBase) return prev;
          // Update all pair names and types
          const { single, newTrackTypes } = updateAllPairNamesOnFullTrackRename(name, trackTypes, prev, canonicalTrackName, fullTrackBase[0], 'Full Track');
          // --- PATCH: set trackTypes directly to newTrackTypes ---
          setTrackTypes(newTrackTypes);
          // --- END PATCH ---
          return { single };
        });
        setTimeout(() => { syncingTrackNameRef.current = false; }, 0);
      });
    }
  }, [onApplyTrackNameToFirstPair, trackTypes, canonicalTrackName]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (uploadMode !== 'single') return;
    const files = queuedFileGroups.single?.files || [];
    const groups = groupFilesByBaseNameAll(files);
    const bases = Object.keys(groups);
    let nextTypes = { ...trackTypes };
    // Only assign a type if a base is truly new
    bases.forEach((base, idx) => {
      if (!nextTypes[base]) {
        const usedTypes = Object.values(nextTypes);
        const type = getFirstAvailableType(usedTypes);
        if (type) {
          nextTypes[base] = type;
        }
      }
    });
    // Remove types for bases that no longer exist
    Object.keys(nextTypes).forEach(base => {
      if (!bases.includes(base)) {
        delete nextTypes[base];
      }
    });
    if (JSON.stringify(nextTypes) !== JSON.stringify(trackTypes)) {
      setTrackTypes(nextTypes);
    }
  }, [uploadMode, trackTypes, queuedFileGroups.single?.files, getFirstAvailableType]);

  // Helper to find the base of the pair with 'Full Track' selected
  function getFullTrackBase(trackTypes, groups) {
    // Find the base with 'Full Track' selected, or fallback to first base
    const fullTrackBase = Object.entries(trackTypes).find(([base, type]) => type === 'Full Track' && groups[base]);
    if (fullTrackBase) return fullTrackBase[0];
    const bases = Object.keys(groups);
    return bases.length > 0 ? bases[0] : null;
  }

  // Handler to apply a chatbot name to the checked batch pair
  const applyNameToCheckedBatchPair = useCallback((name) => {
    if (!name || uploadMode !== 'batch') return;
    if (!localBatchTracks.length) return;
    const idx = batchCheckedIndex;
    const inFocusTrack = localBatchTracks[idx];
    if (!inFocusTrack) return;
    // Update localBatchTracks by id (array-based, like single mode)
    setLocalBatchTracks(prev => {
      const updated = prev.map((t, i) => (i === idx ? { ...t, base: name } : t));
      // Sync queuedFileGroups to match new localBatchTracks
      setQueuedFileGroups(() => {
        const newGroups = {};
        updated.forEach((track, i) => {
          newGroups[track.base] = { wav: track.wav, mp3: track.mp3, order: i };
        });
        return newGroups;
      });
      // Notify parent with new file pairs (array order)
      if (typeof onBatchFilesSelected === 'function') {
        const filePairs = updated.map((track) => ({ id: track.id, base: track.base, wav: track.wav, mp3: track.mp3 }));
        onBatchFilesSelected(filePairs);
      }
      return updated;
    });
    // Move checkmark to next pair (if any)
    setTimeout(() => {
      setBatchCheckedIndex(prevIdx => {
        const nextIdx = prevIdx + 1 < localBatchTracks.length ? prevIdx + 1 : prevIdx;
        return nextIdx;
      });
    }, 0);
  }, [uploadMode, batchCheckedIndex, localBatchTracks, onBatchFilesSelected]);

  // Expose the setter to parent/chatbot
  useEffect(() => {
    if (typeof window !== 'undefined') {
      batchNameSetterRef.current = applyNameToCheckedBatchPair;
    }
    if (typeof onApplyTrackNameToBatchPair === 'function') {
      onApplyTrackNameToBatchPair(applyNameToCheckedBatchPair);
    }
  }, [uploadMode, batchCheckedIndex, localBatchTracks, applyNameToCheckedBatchPair, onApplyTrackNameToBatchPair]);

  // Watch for missing 'Full Track' in single mode and show/hide toast
  useEffect(() => {
    if (uploadMode !== 'single') {
      setShowFullTrackToast(false);
      return;
    }
    const files = queuedFileGroups.single?.files || [];
    const groups = groupFilesByBaseNameAll(files);
    const hasPairs = Object.keys(groups).length > 0;
    const hasFullTrack = Object.entries(trackTypes).some(([, type]) => type === 'Full Track');
    if (hasPairs && !hasFullTrack) {
      setShowFullTrackToast(true);
    } else {
      setShowFullTrackToast(false);
    }
  }, [uploadMode, queuedFileGroups, trackTypes]);

  // Render waveform previews for each pair
  const renderPreviews = () => {
    if (uploadMode === 'batch') {
      return (
        <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', width: '100%', gap: 12, marginLeft: -6, marginRight: -6, marginBottom: 24 }}>
          <style>{`
            @media (max-width: 700px) {
              .batch-preview-card { flex-basis: 100% !important; min-width: 100% !important; max-width: 100% !important; }
            }
            .batch-radio {
              position: absolute;
              left: 8px;
              top: 8px;
              z-index: 3;
            }
          `}</style>
          {localBatchTracks.map((track, idx) => (
            <div
              key={track.id || track.base}
              className="d-flex align-items-center batch-preview-card"
              style={{
                flex: '0 0 100%',
                minWidth: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                background: 'rgba(255,255,255,0.01)',
                border: '1px solid #333',
                borderRadius: 6,
                padding: 16,
                position: 'relative',
                fontSize: 14,
                marginBottom: 16,
              }}
            >
              {/* Top-right: focus selector and X button */}
              <div style={{ display: 'flex', alignItems: 'center', position: 'absolute', top: 8, right: 8 }}>
                <div style={{ marginRight: 4, minWidth: 0, display: 'flex', alignItems: 'center' }}>
                  <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13, userSelect: 'none', marginRight: 4 }}>apply name</span>
                  <Radio
                    checked={batchCheckedIndex === idx}
                    onChange={() => setBatchCheckedIndex(idx)}
                    value={track.base}
                    name="batch-focus-selector"
                    style={{ color: 'var(--accent-1-color)', padding: 0 }}
                    inputProps={{ 'aria-label': `Apply name to ${track.base}` }}
                  />
                </div>
                <IconButton size="small" onClick={() => handleRemoveSelected(track.base)} aria-label="Remove pair" style={{ color: '#fff', alignSelf: 'center' }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', fontWeight: 600, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'rgba(255,255,255,0.3)', fontSize: 15 }}>
                  {editingName === track.base ? (
                    <>
                      <input
                        type="text"
                        value={editedName}
                        autoFocus
                        onChange={e => setEditedName(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            if (editedName && editedName !== track.base) {
                              // Update both localBatchTracks and queuedFileGroups
                              setLocalBatchTracks(prev => prev.map((t, i) => (i === idx ? { ...t, base: editedName } : t)));
                              setQueuedFileGroups(prev => {
                                const newGroups = {};
                                localBatchTracks.forEach((t, i2) => {
                                  const base = i2 === idx ? editedName : t.base;
                                  newGroups[base] = { wav: t.wav, mp3: t.mp3, order: i2 };
                                });
                                return newGroups;
                              });
                              if (typeof onBatchFilesSelected === 'function') {
                                const filePairs = localBatchTracks.map((t, i2) => {
                                  if (i2 === idx) {
                                    return { id: t.id, base: editedName, wav: t.wav, mp3: t.mp3 };
                                  }
                                  return { id: t.id, base: t.base, wav: t.wav, mp3: t.mp3 };
                                });
                                onBatchFilesSelected(filePairs);
                              }
                            }
                            setEditingName(null);
                          } else if (e.key === 'Escape') {
                            setEditingName(null);
                          }
                        }}
                        onBlur={() => setEditingName(null)}
                        style={{
                          fontWeight: 600,
                          fontSize: 15,
                          background: 'rgba(30,30,30,0.85)',
                          color: '#fff',
                          border: '1px solid #555',
                          borderRadius: 4,
                          padding: '2px 8px',
                          marginRight: 8,
                          minWidth: 120,
                          maxWidth: 320,
                        }}
                      />
                      <IconButton size="small"
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => {
                          if (editedName && editedName !== track.base) {
                            setLocalBatchTracks(prev => prev.map((t, i) => (i === idx ? { ...t, base: editedName } : t)));
                            setQueuedFileGroups(prev => {
                              const newGroups = {};
                              localBatchTracks.forEach((t, i2) => {
                                const base = i2 === idx ? editedName : t.base;
                                newGroups[base] = { wav: t.wav, mp3: t.mp3, order: i2 };
                              });
                              return newGroups;
                            });
                            if (typeof onBatchFilesSelected === 'function') {
                              const filePairs = localBatchTracks.map((t, i2) => {
                                if (i2 === idx) {
                                  return { id: t.id, base: editedName, wav: t.wav, mp3: t.mp3 };
                                }
                                return { id: t.id, base: t.base, wav: t.wav, mp3: t.mp3 };
                              });
                              onBatchFilesSelected(filePairs);
                            }
                          }
                          setEditingName(null);
                        }} aria-label="Confirm name" style={{ color: 'var(--accent-1-color)', marginRight: 4 }}>
                        <CheckIcon fontSize="small" />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260, fontSize: 15, color: 'var(--accent-1-color)' }}>{track.base}</span>
                      <IconButton size="small" onClick={() => { setEditingName(track.base); setEditedName(track.base); }} aria-label="Edit name" style={{ color: 'var(--accent-1-color)', marginLeft: 4 }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </div>
                <div className="d-flex flex-row" style={{ gap: 8 }}>
                  {track.wav && (
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <WavePlayer id={`${id}_${track.base}_wav`} blob={track.wav} isActiveTrack={false} />
                    </div>
                  )}
                  {!track.mp3 && (
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>MP3 will be auto-generated</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    } else if (uploadMode === 'single') {
      const files = queuedFileGroups.single?.files || [];
      const groups = groupFilesByBaseNameAll(files);
      return Object.entries(groups).map(([base, pair], idx) => (
        <div key={base} className="mb-4 d-flex align-items-center" style={{ border: '1px solid #333', borderRadius: 6, position: 'relative', padding: 16, background: 'rgba(255,255,255,0.01)', fontSize: 14 }}>
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', fontWeight: 600, marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'rgba(255,255,255,0.3)', fontSize: 17 }}>
              {editingName === base ? (
                <>
                  <input
                    type="text"
                    value={editedName}
                    autoFocus
                    onChange={e => setEditedName(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        if (editedName && editedName !== base) {
                          // Update both files' names for upload
                          updateSingleTrackName(base, editedName, pair);
                        }
                        setEditingName(null);
                      } else if (e.key === 'Escape') {
                        setEditingName(null);
                      }
                    }}
                    onBlur={() => setEditingName(null)}
                    style={{
                      fontWeight: 600,
                      fontSize: 17,
                      background: 'rgba(30,30,30,0.85)',
                      color: '#fff',
                      border: '1px solid #555',
                      borderRadius: 4,
                      padding: '2px 8px',
                      marginRight: 8,
                      minWidth: 140,
                      maxWidth: 340,
                    }}
                  />
                  <IconButton size="small" onMouseDown={e => e.preventDefault()} onClick={() => {
                    if (editedName && editedName !== base) {
                      updateSingleTrackName(base, editedName, pair);
                    }
                    setEditingName(null);
                  }} aria-label="Confirm name" style={{ color: 'var(--accent-1-color)', marginRight: 4 }}>
                    <CheckIcon fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300, fontSize: 17, color: 'var(--accent-1-color)' }}>{base}</span>
                  <IconButton size="small" onClick={() => { setEditingName(base); setEditedName(base); }} aria-label="Edit name" style={{ color: 'var(--accent-1-color)', marginLeft: 4 }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </>
              )}
            </div>
            <div className="d-flex flex-row" style={{ gap: 12 }}>
              {pair.wav && (
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Show original file name above the preview */}
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 2 }}>
                    {files.find(f => f.name.toLowerCase().endsWith('.wav') && f.name.replace(/\.(wav|mp3)$/i, '') === base)?.originalName || `${base}.wav`}
                  </div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>{`${base}.wav`}</div>
                  <WavePlayer id={`${id}_single_${base}_wav`} blob={pair.wav} isActiveTrack={false} />
                </div>
              )}
              {pair.mp3 && (
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Show original file name above the preview */}
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 2 }}>
                    {files.find(f => f.name.toLowerCase().endsWith('.mp3') && f.name.replace(/\.(wav|mp3)$/i, '') === base)?.originalName || `${base}.mp3`}
                  </div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>{`${base}.mp3`}</div>
                  <WavePlayer id={`${id}_single_${base}_mp3`} blob={pair.mp3} isActiveTrack={false} />
                </div>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', position: 'absolute', top: 8, right: 8 }}>
            {/* Label dropdown - hidden in single mode for Spot Management MVP (always Full Track) */}
            {/*
            <div style={{ marginRight: 4, minWidth: 0 }}>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  fontSize: 13,
                  color: '#bbb',
                  background: 'transparent',
                  borderRadius: 4,
                  padding: '2px 8px',
                  userSelect: 'none',
                  border: '1px solid #444',
                  minWidth: 0,
                  maxWidth: 160,
                  whiteSpace: 'nowrap',
                }}
                onClick={e => handleMenuOpen(base, e)}
              >
                {trackTypes[base] || 'Full Track'} <ArrowDropDownIcon style={{ fontSize: 18, marginLeft: 2 }} />
              </span>
              <Menu
                anchorEl={menuAnchorEls[base]}
                keepMounted
                open={Boolean(menuAnchorEls[base])}
                onClose={() => handleMenuClose(base)}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ style: { minWidth: 120, background: 'rgba(96,195,245,0.103)', color: '#fff', boxShadow: '0 5px 15px rgba(0,0,0,0.3)', borderRadius: 4, border: '1px solid rgba(255,255,255,0.1)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }, className: 'frosted-dropdown' }}
              >
                {TRACK_TYPE_OPTIONS.filter(option =>
                  !Object.entries(trackTypes).some(([otherBase, t]) => otherBase !== base && t === option)
                  || trackTypes[base] === option
                ).map(option => (
                  <MenuItem
                    key={option}
                    selected={trackTypes[base] === option || (!trackTypes[base] && option === 'Full Track')}
                    onClick={() => handleTypeSelect(base, option)}
                    style={{ fontSize: 13, color: '#fff', background: trackTypes[base] === option ? 'var(--accent-1-color)' : 'transparent' }}
                    disabled={Object.entries(trackTypes).some(([otherBase, t]) => otherBase !== base && t === option)}
                  >
                    {option}
                  </MenuItem>
                ))}
              </Menu>
            </div>
            */}
            <IconButton size="small" onClick={() => handleRemoveSelected(files.findIndex(f => f.name.replace(/\.(wav|mp3)$/i, '') === base))} aria-label="Remove file" style={{ marginLeft: 4, color: '#fff', alignSelf: 'center' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        </div>
      ));
    }
    return null;
  };

  // Render upload progress bars
  const renderProgress = () => {
    if (uploadMode === 'batch') {
      return Object.entries(currentUploadFileGroups).map(([base, pair]) => (
        <div key={base} className="mb-2">
          <div style={{ color: 'rgba(255,255,255,0.3)' }}>{`${base}.wav`}</div>
          <Progress value={uploadProgress[`${base}.wav`] || 0} className="accent-progress" />
        </div>
      ));
    } else if (uploadMode === 'single') {
      const files = currentUploadFileGroups.single?.files || [];
      return files.map((file) => (
        <div key={file.name} className="mb-2">
          <div style={{ color: 'rgba(255,255,255,0.3)' }}>{file.name}</div>
          <Progress value={uploadProgress[file.name] || 0} className="accent-progress" />
        </div>
      ));
    }
    return null;
  };

  // Restore missing functions for uploader UI
  // Handle mode change with confirmation
  const handleModeChange = (newMode, showModal) => {
    if (newMode === uploadMode) return;
    if (hasQueuedOrUploaded()) {
      setPendingMode(newMode);
      setShowModeConfirm(true);
    } else {
      setUploadMode(newMode);
      setError('');
      if (onModeChange) onModeChange(newMode);
    }
  };

  // Helper: check if there are any queued or uploaded tracks
  const hasQueuedOrUploaded = () => {
    if (uploadMode === 'batch') {
      if (Object.keys(queuedFileGroups).length > 0) return true;
    } else if (uploadMode === 'single') {
      if (queuedFileGroups.single?.files?.length > 0) return true;
    }
    if (uploadedTracks.length > 0) return true;
    return false;
  };

  // Confirm mode change: clear all and switch
  const confirmModeChange = () => {
    setQueuedFileGroups({});
    setUploadedTracks([]);
    setAnalyzedFiles(new Set()); // Clear analyzed files tracking when switching modes
    setUploadMode(pendingMode);
    setError('');
    if (onModeChange) onModeChange(pendingMode);
    setPendingMode(null);
    setShowModeConfirm(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  // Cancel mode change
  const cancelModeChange = () => {
    setPendingMode(null);
    setShowModeConfirm(false);
  };

  // Render uploaded tracks list
  const renderUploadedTracks = () => {
    if (!uploadedTracks.length) return null;
    // Separate batch and single tracks
    const batchTracks = uploadedTracks.filter(t => t.mode === 'batch');
    const singleTracks = uploadedTracks.filter(t => t.mode === 'single');
    // Helper to get the global index in uploadedTracks for a batch card
    const getBatchGlobalIndex = (batchIdx) => {
      let count = 0;
      for (let i = 0; i < uploadedTracks.length; i++) {
        if (uploadedTracks[i].mode === 'batch') {
          if (count === batchIdx) return i;
          count++;
        }
      }
      return -1;
    };
    return (
      <div className="mt-4">
        <style>{tickerCSS}</style>
        <hr />
        <h5>Uploaded Tracks</h5>
        {/* Batch mode uploaded tracks in flex row */}
        {batchTracks.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 12, marginBottom: 24 }}>
            {batchTracks.map((track, batchIdx) => (
              <div
                key={batchIdx}
                className="p-2"
                style={{ border: '1px solid #333', borderRadius: 6, position: 'relative', display: 'flex', flexDirection: 'column', width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.01)' }}
              >
                <IconButton size="small" onClick={() => handleRemoveUploaded(getBatchGlobalIndex(batchIdx))} aria-label="Remove uploaded track" style={removeButtonStyle}>
                  <CloseIcon fontSize="small" />
                </IconButton>
                <div style={{ width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <strong style={{ marginRight: 8, color: 'rgba(255,255,255,0.3)' }}>Name:</strong>
                    <span style={{ ...tickerStyle, color: 'rgba(255,255,255,0.3)' }} className="ticker-hover">
                      <span style={tickerInnerStyle} className="ticker-inner">{track.base}</span>
                    </span>
                  </div>
                  <div className="d-flex flex-row mt-2" style={{ gap: 8 }}>
                    {track.mp3?.downloadUrl ? (
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <WavePlayer id={`uploaded_${batchIdx}_${track.base}_mp3`} file={track.mp3.downloadUrl} isActiveTrack={false} />
                      </div>
                    ) : track.wav?.downloadUrl ? (
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <WavePlayer id={`uploaded_${batchIdx}_${track.base}_wav`} file={track.wav.downloadUrl} isActiveTrack={false} />
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {/* Single mode uploaded tracks stacked vertically */}
        {singleTracks.map((track, idx) => (
          <div key={idx} className="mb-4 p-2" style={{ border: '1px solid #333', borderRadius: 6, position: 'relative', display: 'flex', alignItems: 'center' }}>
            <IconButton size="small" onClick={() => handleRemoveUploaded(uploadedTracks.indexOf(track))} aria-label="Remove uploaded track" style={removeButtonStyleVertical}>
              <CloseIcon fontSize="small" />
            </IconButton>
            <div style={{ width: '100%' }}>
              <div className="d-flex flex-row flex-wrap mt-2">
                {track.files
                  .filter(file => file.fileName?.toLowerCase().endsWith('.mp3'))
                  .map((file, fidx) => (
                    <div key={file.fileName} className="mr-3 mb-2">
                      <div style={tickerStyle} className="ticker-hover">
                        <span style={tickerInnerStyle} className="ticker-inner">{(file.fileName || '').replace(/\.mp3$/i, '')}</span>
                      </div>
                      <WavePlayer id={`uploaded_${idx}_single_${fidx}`} file={file.downloadUrl} isActiveTrack={false} />
                    </div>
                  ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Restore the original return structure
  return (
    <ModalConsumer>
      {({ showModal }) => (
        <Card className="no-bg-card">
          <CardBody>
            {/* Only show mode selector if not in edit mode */}
            {!isEditMode && (
            <FormControl component="fieldset" style={{ marginBottom: 16 }}>
              <RadioGroup
                row
                value={uploadMode}
                onChange={e => handleModeChange(e.target.value, showModal)}
                aria-label="upload mode"
                name="uploadMode"
              >
                <FormControlLabel
                  value="single"
                  control={<Radio style={{ color: 'var(--accent-1-color)' }} />}
                  label="Single"
                  id="radio-single-mode"
                />
                <FormControlLabel
                  value="batch"
                  control={<Radio style={{ color: 'var(--accent-1-color)' }} />}
                  label="Batch"
                  id="radio-batch-mode"
                    disabled={isEditMode}
                />
              </RadioGroup>
              <UncontrolledTooltip placement="top" target="radio-single-mode" delay={{ show: 1500, hide: 0 }} trigger="hover">
                Upload a WAV file for a single spot. MP3 preview is generated automatically.
              </UncontrolledTooltip>
              <UncontrolledTooltip placement="top" target="radio-batch-mode" delay={{ show: 1500, hide: 0 }} trigger="hover">
                Upload multiple audio files to multiple tracks with shared meta data. Track names will be generated from file names
              </UncontrolledTooltip>
            </FormControl>
            )}
            <div
              className="file-input-custom text-left d-flex w-100 flex-column position-relative"
              onDrop={uploadDisabled ? undefined : handleDrop}
              onDragOver={e => e.preventDefault()}
              style={{
                border: `2px dashed ${uploadDisabled ? 'rgba(255, 180, 50, 0.3)' : '#ccc'}`,
                padding: 20,
                borderRadius: 8,
                cursor: uploadDisabled ? 'not-allowed' : 'pointer',
                opacity: uploadDisabled ? 0.6 : 1,
              }}
              onClick={uploadDisabled ? undefined : handleClick}
            >
              <input
                type="file"
                accept=".wav"
                multiple
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={handleChange}
                id={id}
                disabled={uploadDisabled}
              />
              <div style={{ minHeight: 40, color: uploadDisabled ? '#ffb432' : 'rgba(255, 255, 255, 0.5)', fontSize: '12px' }}>
                {uploadDisabled
                  ? (uploadDisabledMessage || 'Upload disabled.')
                  : uploadDisabledMessage
                    ? uploadDisabledMessage
                    : 'Drag and drop a .wav file here, or click to select. An MP3 preview will be generated automatically.'
                }
              </div>
            </div>
            {error && <div className="text-danger mt-2">{error}</div>}
            {((uploadMode === 'batch' && Object.keys(queuedFileGroups).length > 0) || (uploadMode === 'single' && (queuedFileGroups.single?.files?.length > 0))) && (
              <div className="mt-3">
                <h5>Track Previews</h5>
                {renderPreviews()}
                {/* TODO: Add editable track name fields here in a later step */}
                <Button
                  className="btn-round mt-3 btn-success-custom"
                  size="md"
                  onClick={handleUpload}
                  disabled={uploading}
                  style={{ position: 'relative', zIndex: 2 }}
                >
                  <span style={{ position: 'relative', zIndex: 3 }}>Upload All</span>
                </Button>
              </div>
            )}
            {uploading && (
              <div className="mt-3">
                <h5>Upload Progress</h5>
                {renderProgress()}
              </div>
            )}
            {/* Only show Uploaded Tracks card for batch mode */}
            {uploadMode === 'batch' && renderUploadedTracks()}
            {showModeConfirm && (
              <GenericConfirmModal
                isOpen={true}
                title="Change Upload Mode?"
                body={
                  <div>
                    <p style={{ color: '#fff' }}>
                      Changing upload mode will <b>remove all queued and uploaded tracks</b>.<br />
                      Are you sure you want to continue?
                    </p>
                  </div>
                }
                callback={confirmModeChange}
                cancelCallback={cancelModeChange}
              />
            )}
            {showBatchToast && (
              <div className="toast-container">
                <Alert color="success" isOpen toggle={() => setShowBatchToast(false)} className="alert-toast">
                  <strong>Batch Upload</strong><br />
                  Batch upload detected: {batchToastCount} tracks will be created.
                </Alert>
              </div>
            )}
            {showFullTrackToast && (
              <div className="toast-container">
                <Alert color="warning" isOpen className="alert-toast toast-warning">
                  <strong>Track Name Warning</strong><br />
                  Please select <b>'Full Track'</b> for one pair to ensure correct naming and upload.
                </Alert>
              </div>
            )}
            <style>{`
              .accent-progress .progress-bar {
                background-color: var(--accent-1-color) !important;
              }
              .accent-progress {
                background-color: rgba(255, 255, 255, 0.05) !important;
              }
            `}</style>
          </CardBody>
        </Card>
      )}
    </ModalConsumer>
  );
});

export default MultiFileUploader; 